// Built-in apps as they appear in the Connections grid: logo, department, and search keywords
// — so they behave like any Composio toolkit.
//
// This file is the ONE place in the web client that knows app ids. Everything else is driven by
// GET /api/morrow/apps, so a new built-in app shows up in the grid, gets a connect form built
// from its own field list, and connects, without any web change at all.
//
// That made the old `builtinAppMatches` a trap: it read `META[app.id]?.department` and compared
// it to the active filter, so an app with NO entry here answered `false` for EVERY department —
// it vanished from every filtered view and could only be found on "All". The app worked; you
// just couldn't reach it. Idefix shipped in exactly that state.
//
// A missing entry now falls back to the `other` department instead of hiding (see
// `builtinAppMatches`), and `builtinApps.test.ts` pins that property against a synthetic unknown
// app rather than against the apps that happen to exist today. `other` rather than "every
// department" on review: showing one connector under all eight chips implies a categorisation
// that isn't there, and `other` is already the bucket the server's own toolkit classifier uses
// when nothing matches. Every app below has a real entry, so the fallback is a safety net for
// the NEXT connector, not a thing any shipped app relies on.
import type { ToolkitDepartmentFilterId } from "./ConnectionsSettings.logic";

interface BuiltinAppMeta {
  /** Omit when there is no asset: the tile then renders a monogram with no failed request. */
  readonly logo?: string;
  readonly department: ToolkitDepartmentFilterId;
  /** Extra words people search with (Turkish and English). */
  readonly keywords: readonly string[];
}

/** Words that should find ANY Turkish marketplace connector, shared so they stay in step. */
const MARKETPLACE_KEYWORDS = [
  "pazaryeri",
  "pazar yeri",
  "marketplace",
  "e-ticaret",
  "eticaret",
  "ecommerce",
  "sipariş",
  "siparis",
  "order",
  "stok",
  "stock",
  "ürün",
  "urun",
  "product",
  "kargo",
  "iade",
] as const;

const META: Record<string, BuiltinAppMeta> = {
  dia: {
    logo: "/brand/apps/dia.png",
    department: "finance",
    keywords: ["erp", "muhasebe", "fatura", "invoice", "cari", "stok", "accounting", "e-fatura"],
  },
  entegra: {
    logo: "/brand/apps/entegra.png",
    department: "crm",
    keywords: [
      ...MARKETPLACE_KEYWORDS,
      "trendyol",
      "hepsiburada",
      "n11",
      "amazon",
      "entegrasyon",
      "hub",
    ],
  },
  trendyol: {
    logo: "/brand/apps/trendyol.png",
    department: "crm",
    keywords: [...MARKETPLACE_KEYWORDS, "trendyol", "ty", "paket", "package"],
  },
  icecat: {
    logo: "/brand/apps/icecat.png",
    // Product CONTENT rather than a marketplace, so it files under data, not crm.
    department: "data",
    keywords: [
      "icecat",
      "ürün bilgisi",
      "urun bilgisi",
      "product content",
      "datasheet",
      "barkod",
      "barcode",
      "gtin",
      "ean",
      "özellik",
      "ozellik",
      "specs",
      "katalog",
      "catalog",
    ],
  },
  n11: {
    logo: "/brand/apps/n11.png",
    department: "crm",
    keywords: [...MARKETPLACE_KEYWORDS, "n11", "iade", "return"],
  },
  ciceksepeti: {
    logo: "/brand/apps/ciceksepeti.png",
    department: "crm",
    keywords: [...MARKETPLACE_KEYWORDS, "çiçeksepeti", "ciceksepeti", "çiçek", "cicek", "flower"],
  },
  hepsiburada: {
    logo: "/brand/apps/hepsiburada.png",
    department: "crm",
    keywords: [...MARKETPLACE_KEYWORDS, "hepsiburada", "hb", "hepsijet", "paket", "package"],
  },
  mediamarkt: {
    logo: "/brand/apps/mediamarkt.png",
    department: "crm",
    keywords: [
      ...MARKETPLACE_KEYWORDS,
      "mediamarkt",
      "media markt",
      "mirakl",
      "elektronik",
      "electronics",
    ],
  },
  pazarama: {
    logo: "/brand/apps/pazarama.png",
    department: "crm",
    keywords: [...MARKETPLACE_KEYWORDS, "pazarama", "iş ortağım", "is ortagim"],
  },
  idefix: {
    logo: "/brand/apps/idefix.png",
    department: "crm",
    keywords: [...MARKETPLACE_KEYWORDS, "idefix", "kitap", "book"],
  },
};

export function builtinAppLogo(appId: string): string | undefined {
  return META[appId]?.logo;
}

/**
 * The display name for a built-in app, for surfaces that only have its id.
 *
 * The Connections grid gets names from `/api/morrow/apps`, but the agent's app picker holds
 * nothing but slugs, and rendering "ciceksepeti" or "mediamarkt" to a seller reads as a bug.
 * Kept here beside the logos rather than fetched, since a picker should not need a second
 * round-trip to spell a name it is about to show.
 */
const LABELS: Record<string, string> = {
  dia: "DIA",
  entegra: "Entegra",
  trendyol: "Trendyol",
  hepsiburada: "Hepsiburada",
  n11: "n11",
  ciceksepeti: "Çiçeksepeti",
  idefix: "Idefix",
  pazarama: "Pazarama",
  mediamarkt: "MediaMarkt",
  icecat: "Icecat",
};

/** The app's proper name, or null when this is not a built-in app id. */
export function builtinAppLabel(appId: string): string | null {
  return LABELS[appId] ?? null;
}

/** True for an id our own connectors own — i.e. not a Composio toolkit slug. */
export function isBuiltinAppId(appId: string): boolean {
  return appId in LABELS;
}

/** The department a built-in app files under when this file has no entry for it. */
const FALLBACK_DEPARTMENT: ToolkitDepartmentFilterId = "other";

/**
 * Does a built-in app belong in the grid for this search + department?
 *
 * An app with no META entry files under `other` rather than vanishing. Appearing under a chip
 * it doesn't perfectly belong to costs a moment of confusion; being absent from every chip
 * makes a working integration unreachable, which is what used to happen.
 */
export function builtinAppMatches(
  app: { id: string; name: string; description: string },
  query: string,
  department: ToolkitDepartmentFilterId,
): boolean {
  const meta = META[app.id];
  // An app with no entry is treated as `other`, never as "no department" — the latter is what
  // hid Idefix from every filtered view.
  const appDepartment = meta?.department ?? FALLBACK_DEPARTMENT;
  if (department !== "all" && appDepartment !== department) return false;
  const q = query.trim().toLocaleLowerCase("tr");
  if (!q) return true;
  const haystack = [app.id, app.name, app.description, ...(meta?.keywords ?? [])]
    .join(" ")
    .toLocaleLowerCase("tr");
  return haystack.includes(q);
}
